import { memo } from 'react';
import { EmptyState } from '../Kit/_index';
import { useVirtualList } from './useVirtualList';

const BLOCK = 'LStudio__app_virtual';

/*
    🔥 FILA MEMOIZADA

    ⚠️ POR QUÉ ACÁ SÍ HACE FALTA memo

    En una lista virtual, CADA frame de scroll cambia el estado del
    contenedor y React vuelve a renderizar las 20 filas visibles.
    Diecinueve de esas veinte no cambiaron nada: son las mismas de
    hace un milisegundo, corridas.

    Con memo, solo se renderiza la que entró y la que salió. Es la
    diferencia entre 20 renders por frame y 2.

    ⚠️ PARA QUE memo SIRVA, renderItem TIENE QUE SER ESTABLE

    Si se lo pasás como función anónima creada en el render del padre,
    cambia de identidad en cada vuelta y memo no ahorra nada. Envolvelo
    en useCallback:

        const onRenderRow = useCallback((item) => <Fila item={item} />, []);
*/
const VirtualRow = memo(({ item, index, offset, rowHeight, renderItem }) => (

    <div
        className={`${BLOCK}__row`}
        style={{ transform: `translateY(${offset}px)`, height: rowHeight }}
    >
        {renderItem(item, index)}
    </div>
));

VirtualRow.displayName = 'VirtualRow';

/*
    🔥 LISTA VIRTUALIZADA

    ⚠️ EL ESPACIADOR VA CON position:absolute, NO CON padding

    Con padding-top el navegador recalcula el layout del contenedor en
    cada frame de scroll. Con posición absoluta, cada fila se ubica con
    transform y el navegador solo compone: es la diferencia entre 60
    cuadros por segundo y un scroll a los tirones.

    ⚠️ CADA FILA NECESITA SU ALTURA EXACTA

    Se la pone el componente, no el contenido. Si la fila desborda,
    tapa a la siguiente en vez de empujarla, porque el orden lo dicta
    la matemática y no el flujo.
*/

export const VirtualList = ({
    items = [],
    renderItem,
    rowHeight = 44,
    height = 400,
    overscan = 5,
    getKey = null,
    emptyLabel = 'Sin resultados',
    className = '',
}) => {

    const virtual = useVirtualList({ count: items.length, rowHeight, overscan });

    if (!items.length) return <EmptyState title={emptyLabel} size="sm" />;

    return (
        <div
            ref={virtual.scrollRef}
            className={`${BLOCK} ${className}`}
            style={{ height }}
            onScroll={virtual.onScroll}
        >

            <div className={`${BLOCK}__inner`} style={{ height: virtual.totalHeight }}>
                {
                    virtual.items.map(({ index, offset }) => {

                        const ITEM = items[index];

                        return (
                            <VirtualRow
                                key={getKey?.(ITEM, index) ?? ITEM?.id ?? index}
                                item={ITEM}
                                index={index}
                                offset={offset}
                                rowHeight={rowHeight}
                                renderItem={renderItem}
                            />
                        );
                    })
                }
            </div>

        </div>
    );
};

/*
    🔥 FILA DE TABLA MEMOIZADA

    ⚠️ columns TIENE QUE SER ESTABLE

    Es el error más común al usar esto: definir el array de columnas
    dentro del render del padre. Cambia de identidad en cada vuelta,
    memo lo ve como una prop nueva y no ahorra nada.

    Definilo fuera del componente, o con useMemo.
*/
const VirtualTableRow = memo(({ row, index, offset, rowHeight, columns, onRowClick }) => (

    <div
        role="row"
        className={`${BLOCK}_table__row`}
        data-clickable={!!onRowClick}
        tabIndex={onRowClick ? 0 : undefined}
        style={{ transform: `translateY(${offset}px)`, height: rowHeight }}
        onClick={onRowClick ? () => onRowClick(row, index) : undefined}
        onKeyDown={
            onRowClick
                ? (e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    onRowClick(row, index);
                }
                : undefined
        }
    >
        {
            columns.map((column) => (
                <span key={column.key} role="cell"
                    className={`${BLOCK}_table__td`}
                    data-align={column.align ?? 'left'} >
                    {column.render ? column.render(row, index) : row?.[column.key]}
                </span>
            ))
        }
    </div>
));

VirtualTableRow.displayName = 'VirtualTableRow';

/*
    🔥 TABLA VIRTUALIZADA

    columns: [{ key, label, width, align, render }]

    ⚠️ NO ES UN <table>

    Una tabla real no se puede virtualizar: el navegador necesita todas
    las filas para calcular el ancho de las columnas. Por eso esto es
    una grilla CSS con role="table", que un lector de pantalla anuncia
    igual pero deja controlar el ancho a mano.

    ⚠️ ESTO NO REEMPLAZA A TU DataTable

    La tabla que ya tenés hace ordenamiento, selección, exportación,
    importación, 16 estéticas y vista de tarjetas. Esto es para el caso
    puntual de miles de filas donde nada de eso importa tanto como que
    el scroll no se trabe.
*/
export const VirtualTable = ({
    rows = [],
    columns = [],
    rowHeight = 42,
    height = 440,
    overscan = 5,
    getKey = null,
    onRowClick = null,
    emptyLabel = 'Sin resultados',
    stickyHeader = true,
}) => {

    const virtual = useVirtualList({ count: rows.length, rowHeight, overscan });

    const TEMPLATE = columns.map((c) => c.width ?? 'minmax(0, 1fr)').join(' ');

    if (!rows.length) return <EmptyState title={emptyLabel} size="sm" />;

    return (
        <div className={`${BLOCK}_table`} role="table" style={{ '--LS__virtual_cols': TEMPLATE }}>

            <div className={`${BLOCK}_table__head`} role="row" data-sticky={stickyHeader}>
                {
                    columns.map((column) => (
                        <span key={column.key} role="columnheader"
                            className={`${BLOCK}_table__th`} data-align={column.align ?? 'left'} >
                            {column.label}
                        </span>
                    ))
                }
            </div>

            <div
                ref={virtual.scrollRef}
                className={`${BLOCK}_table__scroll`}
                style={{ height }}
                onScroll={virtual.onScroll}
            >

                <div className={`${BLOCK}__inner`} style={{ height: virtual.totalHeight }}>
                    {
                        virtual.items.map(({ index, offset }) => {

                            const ROW = rows[index];

                            return (
                                <VirtualTableRow
                                    key={getKey?.(ROW, index) ?? ROW?.id ?? index}
                                    row={ROW}
                                    index={index}
                                    offset={offset}
                                    rowHeight={rowHeight}
                                    columns={columns}
                                    onRowClick={onRowClick}
                                />
                            );
                        })
                    }
                </div>

            </div>

            <p className={`${BLOCK}_table__foot`}>
                {rows.length.toLocaleString('es-AR')} filas — se pintan{' '}
                {virtual.items.length} en el DOM
            </p>

        </div>
    );
};
